import React, { useCallback, useEffect, useState } from "react";
import { FaArrowLeft, FaPen, FaPlus, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Drawer, PageHead } from "../work/WorkUi";
import { useNotify } from "../../components/ConfirmModal";
import { errorMessage, staffDmApi } from "./dmApi";
import { GroupServices, SERVICE_LABELS, ownerLabel } from "./components/setup/setupUi";
import "./dm.css";

const blankField = () => ({ key: "", label: "", type: "text", required: false, options: "", placeholder: "", pattern: "" });

function stepForm(step, categoryId) {
  return {
    Step_Id: step?.Step_Id || null,
    Category_Id: step?.Category_Id || categoryId || "",
    Code: step?.Code || "",
    Title: step?.Title || "",
    Description: step?.Description || "",
    Client_Notes: step?.Client_Notes || "",
    Instructions: step?.Instructions || "",
    Help_Url: step?.Help_Url || "",
    Help_Label: step?.Help_Label || "",
    Open_Url: step?.Open_Url || "",
    Services: step?.Services || [],
    Applies_To: step?.Applies_To || "Any",
    Depends_On: (step?.Depends_On || []).join(", "),
    Weight: step?.Weight || 1,
    Default_Owner: step?.Default_Owner || "Agency",
    Is_Optional: Boolean(step?.Is_Optional),
    Verify_Type: step?.Verify_Type || "manual",
    Sort_Order: step?.Sort_Order ?? "",
    Status: step?.Status || "Active",
    Snippet: step?.Snippet || "",
    Fields: (step?.Fields || []).map((f) => ({
      ...blankField(),
      ...f,
      options: (f.options || []).join(", "),
    })),
  };
}

function ServicePicker({ value, onChange, services }) {
  const toggle = (s) => onChange(value.includes(s) ? value.filter((v) => v !== s) : [...value, s]);
  return (
    <div className="dmp-actions">
      {services.map((s) => (
        <label key={s} className="dms-check">
          <input type="checkbox" checked={value.includes(s)} onChange={() => toggle(s)} />
          <span>{SERVICE_LABELS[s] || s}</span>
        </label>
      ))}
      <span className="dmp-muted">{value.length ? "" : "None ticked = every DM client"}</span>
    </div>
  );
}

function StepEditor({ api, meta, categories, initial, onClose, onSaved }) {
  const notify = useNotify();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const setField = (i, key, value) =>
    setForm((f) => ({ ...f, Fields: f.Fields.map((x, j) => (j === i ? { ...x, [key]: value } : x)) }));

  const save = async () => {
    setSaving(true);
    setError("");
    const body = {
      ...form,
      Category_Id: Number(form.Category_Id) || null,
      Depends_On: form.Depends_On.split(",").map((s) => s.trim()).filter(Boolean),
      Weight: Number(form.Weight) || 1,
      Sort_Order: form.Sort_Order === "" ? undefined : Number(form.Sort_Order),
      Fields: form.Fields.filter((f) => f.key || f.label).map((f) => ({
        key: f.key.trim(),
        label: f.label.trim(),
        type: f.type,
        required: Boolean(f.required),
        ...(f.type === "select" ? { options: f.options.split(",").map((s) => s.trim()).filter(Boolean) } : {}),
        ...(f.placeholder ? { placeholder: f.placeholder } : {}),
        ...(f.pattern ? { pattern: f.pattern } : {}),
      })),
    };
    delete body.Step_Id;
    try {
      if (form.Step_Id) await api.updateSetupTemplateStep(form.Step_Id, body);
      else await api.createSetupTemplateStep(body);
      notify.success("Template step saved");
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Could not save the step"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer title={form.Step_Id ? `Edit step: ${initial.Title}` : "New setup step"} onClose={onClose}>
      <div className="dms-drawer">
        {error ? <div className="dmp-error">{error}</div> : null}
        <div className="dmp-form-grid">
          <label className="dmp-field">
            <span>Group</span>
            <select value={form.Category_Id} onChange={set("Category_Id")}>
              <option value="">Select…</option>
              {categories.map((c) => (
                <option key={c.Category_Id} value={c.Category_Id}>
                  {c.Name}
                </option>
              ))}
            </select>
          </label>
          <label className="dmp-field">
            <span>Code (cannot change later)</span>
            <input value={form.Code} onChange={set("Code")} disabled={!!form.Step_Id} placeholder="snapchat_profile" />
          </label>
          <label className="dmp-field dmp-field--full">
            <span>Title</span>
            <input value={form.Title} onChange={set("Title")} />
          </label>
          <label className="dmp-field dmp-field--full">
            <span>Short description</span>
            <input value={form.Description} onChange={set("Description")} />
          </label>
          <label className="dmp-field dmp-field--full">
            <span>What we need from the client (shown to staff and the client)</span>
            <textarea rows={5} value={form.Client_Notes} onChange={set("Client_Notes")} placeholder="- Logo as SVG / AI and a transparent PNG (1000 x 1000 px)" />
          </label>
          <label className="dmp-field dmp-field--full">
            <span>Instructions for the team</span>
            <textarea rows={3} value={form.Instructions} onChange={set("Instructions")} />
          </label>
          <label className="dmp-field">
            <span>Create / help link</span>
            <input value={form.Help_Url} onChange={set("Help_Url")} placeholder="https://…" />
          </label>
          <label className="dmp-field">
            <span>Link label</span>
            <input value={form.Help_Label} onChange={set("Help_Label")} placeholder="Create account" />
          </label>
          <label className="dmp-field dmp-field--full">
            <span>Open account link: {"{field_key}"}, filters |digits |handle, alternatives with ||</span>
            <input
              value={form.Open_Url}
              onChange={set("Open_Url")}
              placeholder="https://x.com/{handle|handle} || {profile_url}"
            />
          </label>
          <div className="dmp-field dmp-field--full">
            <span>Only for these services</span>
            <ServicePicker value={form.Services} onChange={set("Services")} services={meta.dm_services || []} />
          </div>
          <label className="dmp-field">
            <span>Applies to</span>
            <select value={form.Applies_To} onChange={set("Applies_To")}>
              {(meta.applies_to || []).map((a) => (
                <option key={a}>{a}</option>
              ))}
            </select>
          </label>
          <label className="dmp-field">
            <span>Default owner</span>
            <select value={form.Default_Owner} onChange={set("Default_Owner")}>
              {(meta.owners || []).map((o) => (
                <option key={o} value={o}>
                  {ownerLabel(o)}
                </option>
              ))}
            </select>
          </label>
          <label className="dmp-field">
            <span>Depends on (step codes)</span>
            <input value={form.Depends_On} onChange={set("Depends_On")} placeholder="brand_gmail, fb_page" />
          </label>
          <label className="dmp-field">
            <span>Automatic check</span>
            <select value={form.Verify_Type} onChange={set("Verify_Type")}>
              {(meta.verify_types || []).map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </label>
          <label className="dmp-field">
            <span>Weight (1-10)</span>
            <input type="number" min={1} max={10} value={form.Weight} onChange={set("Weight")} />
          </label>
          <label className="dmp-field">
            <span>Sort order</span>
            <input type="number" value={form.Sort_Order} onChange={set("Sort_Order")} />
          </label>
          <label className="dmp-field">
            <span>Status</span>
            <select value={form.Status} onChange={set("Status")}>
              {(meta.template_statuses || ["Active", "Inactive"]).map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="dms-check">
            <input type="checkbox" checked={form.Is_Optional} onChange={set("Is_Optional")} />
            <span>Optional (not counted in %)</span>
          </label>
        </div>

        <h4 className="dms-drawer__h">Fields to capture</h4>
        <div className="dms-tpl-fields">
          {form.Fields.map((f, i) => (
            <div key={i} className="dms-tpl-field">
              <input aria-label="Key" placeholder="key" value={f.key} onChange={(e) => setField(i, "key", e.target.value)} />
              <input aria-label="Label" placeholder="Label" value={f.label} onChange={(e) => setField(i, "label", e.target.value)} />
              <select aria-label="Type" value={f.type} onChange={(e) => setField(i, "type", e.target.value)}>
                {(meta.field_types || []).map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <input
                aria-label={f.type === "select" ? "Options" : "Placeholder"}
                placeholder={f.type === "select" ? "Option A, Option B" : "Placeholder"}
                value={f.type === "select" ? f.options : f.placeholder}
                onChange={(e) => setField(i, f.type === "select" ? "options" : "placeholder", e.target.value)}
              />
              <label className="dms-check">
                <input type="checkbox" checked={Boolean(f.required)} onChange={(e) => setField(i, "required", e.target.checked)} />
                <span>Required</span>
              </label>
              <button
                type="button"
                className="dms-icon-btn"
                aria-label="Remove field"
                onClick={() => setForm((x) => ({ ...x, Fields: x.Fields.filter((_, j) => j !== i) }))}
              >
                <FaTrash aria-hidden="true" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="dmp-btn dmp-btn--ghost dmp-btn--sm"
            onClick={() => setForm((x) => ({ ...x, Fields: [...x.Fields, blankField()] }))}
          >
            <FaPlus aria-hidden="true" /> Add field
          </button>
        </div>

        <h4 className="dms-drawer__h">Install snippet (optional, uses {"{field_key}"})</h4>
        <label className="dmp-field dmp-field--full">
          <span className="dms-sr">Snippet</span>
          <textarea rows={4} className="dms-code-input" value={form.Snippet} onChange={set("Snippet")} />
        </label>

        <div className="dmp-form-actions">
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="dmp-btn" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save step"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}

function CategoryEditor({ api, meta, initial, onClose, onSaved }) {
  const notify = useNotify();
  const [form, setForm] = useState({
    Category_Id: initial?.Category_Id || null,
    Code: initial?.Code || "",
    Name: initial?.Name || "",
    Description: initial?.Description || "",
    Services: initial?.Services || [],
    Sort_Order: initial?.Sort_Order ?? "",
    Status: initial?.Status || "Active",
  });
  const [error, setError] = useState("");
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const save = async () => {
    setError("");
    const body = { ...form, Sort_Order: form.Sort_Order === "" ? undefined : Number(form.Sort_Order) };
    delete body.Category_Id;
    try {
      if (form.Category_Id) {
        delete body.Code;
        await api.updateSetupCategory(form.Category_Id, body);
      } else {
        await api.createSetupCategory(body);
      }
      notify.success("Group saved");
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Could not save the group"));
    }
  };

  return (
    <Drawer title={form.Category_Id ? `Edit group: ${initial.Name}` : "New group"} onClose={onClose}>
      <div className="dms-drawer">
        {error ? <div className="dmp-error">{error}</div> : null}
        <div className="dmp-form-grid">
          <label className="dmp-field">
            <span>Code</span>
            <input value={form.Code} onChange={set("Code")} disabled={!!form.Category_Id} placeholder="orm" />
          </label>
          <label className="dmp-field">
            <span>Name</span>
            <input value={form.Name} onChange={set("Name")} placeholder="Reputation (ORM)" />
          </label>
          <label className="dmp-field dmp-field--full">
            <span>Description</span>
            <input value={form.Description} onChange={set("Description")} />
          </label>
          <div className="dmp-field dmp-field--full">
            <span>Show for these services</span>
            <ServicePicker value={form.Services} onChange={set("Services")} services={meta.dm_services || []} />
          </div>
          <label className="dmp-field">
            <span>Sort order</span>
            <input type="number" value={form.Sort_Order} onChange={set("Sort_Order")} />
          </label>
          <label className="dmp-field">
            <span>Status</span>
            <select value={form.Status} onChange={set("Status")}>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </label>
        </div>
        <div className="dmp-form-actions">
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="dmp-btn" onClick={save}>
            Save group
          </button>
        </div>
      </div>
    </Drawer>
  );
}

/** Staff: the master setup checklist every DM client's Setup tab is built from. */
function SetupTemplateAdmin({ api = staffDmApi }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(
    () =>
      api
        .setupTemplate()
        .then(setData)
        .catch((err) => setError(errorMessage(err, "Could not load the setup template"))),
    [api]
  );

  useEffect(() => {
    load();
  }, [load]);

  const saved = () => {
    setEditing(null);
    load();
  };

  const meta = data?.Meta || {};
  const categories = data?.Categories || [];

  return (
    <div className="dmp dms">
      <div className="dmc-topbar">
        <Link to="/dm-projects" className="dmc-back">
          <FaArrowLeft aria-hidden="true" /> Marketing Profiles
        </Link>
      </div>
      <PageHead
        title="DM Setup Template"
        info="Groups and steps every Digital Marketing client's Setup tab is built from. Groups and steps show only for the services they are ticked for. Changes apply to every client the next time their Setup tab opens; deactivate instead of deleting so saved data is kept."
      >
        <button type="button" className="dmp-btn" onClick={() => setEditing({ kind: "category", value: null })}>
          <FaPlus aria-hidden="true" /> New group
        </button>
      </PageHead>
      {error ? <div className="dmp-error">{error}</div> : null}
      {!data && !error ? <div className="dmp-card dmp-empty">Loading template…</div> : null}

      {categories.map((c) => (
        <section key={c.Category_Id} className={`dmp-section is-open dms-tpl-cat${c.Status !== "Active" ? " dms-tpl-cat--inactive" : ""}`}>
          <div className="dmp-section__head">
            <span className="dmp-section__title dms-cat__title">
              <span className="dms-cat__name">
                {c.Name}
                <GroupServices services={c.Services} code={c.Code} name={c.Name} />
                {c.Status !== "Active" ? <span className="dms-tag">inactive</span> : null}
              </span>
              {c.Description ? <span className="dms-cat__desc">{c.Description}</span> : null}
            </span>
            <button type="button" className="dmp-btn dmp-btn--ghost dms-cat__action" onClick={() => setEditing({ kind: "category", value: c })}>
              <FaPen aria-hidden="true" /> Edit group
            </button>
            <button
              type="button"
              className="dmp-btn dms-cat__action"
              onClick={() => setEditing({ kind: "step", value: stepForm(null, c.Category_Id) })}
            >
              <FaPlus aria-hidden="true" /> Add step
            </button>
          </div>
          <div>
            {c.Steps.length === 0 ? <p className="dmp-muted dms-cat__empty">No steps in this group.</p> : null}
            {c.Steps.map((s) => (
              <div key={s.Step_Id} className={`dms-tpl-row${s.Status !== "Active" ? " is-inactive" : ""}`}>
                <span className="dmp-muted">{s.Sort_Order}</span>
                <span>
                  <strong>{s.Title}</strong>
                  {s.Is_Optional ? <span className="dms-tag">optional</span> : null}
                  {s.Client_Notes ? <span className="dms-tag dms-tag--input">client notes</span> : null}
                  <br />
                  <code>{s.Code}</code>
                </span>
                <span>{s.Services.length ? s.Services.map((x) => SERVICE_LABELS[x] || x).join(", ") : "Group default"}</span>
                <span>{ownerLabel(s.Default_Owner)}</span>
                <span className="dmp-muted">{s.Verify_Type}</span>
                <span>{s.Status}</span>
                <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={() => setEditing({ kind: "step", value: stepForm(s) })}>
                  Edit
                </button>
              </div>
            ))}
          </div>
        </section>
      ))}

      {editing?.kind === "step" ? (
        <StepEditor api={api} meta={meta} categories={categories} initial={editing.value} onClose={() => setEditing(null)} onSaved={saved} />
      ) : null}
      {editing?.kind === "category" ? (
        <CategoryEditor api={api} meta={meta} initial={editing.value} onClose={() => setEditing(null)} onSaved={saved} />
      ) : null}
    </div>
  );
}

export default SetupTemplateAdmin;
